/**
 * Client-side home-region detection from the browser's IANA timezone.
 *
 * Option F.3 (multi-region cleanup): replaces the CF Worker `/route`
 * call. The Worker derived home_region from `cf-ipcountry` + an
 * optional timezone hint; we keep the timezone signal (stable across
 * VPN / travel / airport WiFi — the failure modes that misroute
 * cf-ipcountry) and drop the IP fallback. An IP lookup would add a
 * network round-trip to signup we don't want, and a misrouted user
 * just pays trans-Atlantic latency until sign-out + sign-in. The
 * cache is cleared on sign-out (see `logout.ts`), so the timezone
 * is re-detected on the next sign-in.
 *
 * Mapping must stay in lock-step with the Worker's `resolveRegionFromTimezone`
 * in `workers/route-resolver/src/geo-ip.ts`. Drift here means desktop and
 * iOS clients get told contradictory home_regions depending on which
 * surface hits the server first.
 *
 * Policy:
 *   - `Europe/*` → "eu" (covers all 27 EU + EEA + UK + CH zones at
 *     once, ~50 IANA names). Also includes non-EU European zones
 *     (Turkey, Russia, Balkans, Belarus, Moldova, Ukraine) —
 *     intentional EU-affinity by infrastructure proximity.
 *   - `Atlantic/{Reykjavik,Canary,Madeira,Azores,Faroe}` → "eu"
 *     (Iceland + Spanish/Portuguese EU outermost regions + Faroes).
 *   - `Africa/{Casablanca,Algiers,Tunis,Ceuta}` → "eu" (Maghreb +
 *     Spanish exclave).
 *   - Legacy aliases `GB`, `GB-Eire`, `Eire`, `W-SU` → "eu".
 *   - Everything else (`America/*`, `Asia/*`, `Pacific/*`, `Australia/*`,
 *     `UTC`, `GMT`, etc.) → "us". The Worker returns `null` for these and
 *     falls through to cf-ipcountry; we have no fallback so we default
 *     to "us" — the US authority serves every workspace, so a misrouted
 *     EU user is just slow until they sign out + sign in from EU.
 */

export type HomeRegion = "us" | "eu";

const ATLANTIC_EU_ZONES = new Set<string>([
  "Atlantic/Reykjavik", // Iceland
  "Atlantic/Canary", // Spain — Canary Islands
  "Atlantic/Madeira", // Portugal — Madeira
  "Atlantic/Azores", // Portugal — Azores
  "Atlantic/Faroe", // Denmark — Faroe Islands
]);

const AFRICA_EU_ZONES = new Set<string>([
  "Africa/Casablanca", // Morocco
  "Africa/Algiers", // Algeria
  "Africa/Tunis", // Tunisia
  "Africa/Ceuta", // Spain — Spanish exclave
]);

const LEGACY_EU_ALIASES = new Set<string>([
  "GB",
  "GB-Eire",
  "Eire",
  "W-SU", // Moscow legacy alias
]);

/**
 * Pure helper exposed for tests — maps an explicit timezone string to
 * a region. The runtime path goes through `detectHomeRegion()` which
 * reads `Intl.DateTimeFormat().resolvedOptions().timeZone` first.
 */
export function regionFromTimezone(
  timezone: string | null | undefined,
): HomeRegion {
  if (!timezone || typeof timezone !== "string") return "us";
  const tz = timezone.trim();
  if (tz.length === 0) return "us";
  // Anchor `Europe/` strictly (avoids matching the imaginary `Europe`
  // bare zone or anything starting with `EuropeFoo`).
  if (tz.startsWith("Europe/")) return "eu";
  if (ATLANTIC_EU_ZONES.has(tz)) return "eu";
  if (AFRICA_EU_ZONES.has(tz)) return "eu";
  if (LEGACY_EU_ALIASES.has(tz)) return "eu";
  return "us";
}

/**
 * Synchronously detect the caller's home region from the runtime
 * timezone. No network. Falls back to "us" when `Intl` is unavailable
 * (very old environments) or throws.
 */
export function detectHomeRegion(): HomeRegion {
  let timezone: string | null = null;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (typeof tz === "string" && tz.length > 0) {
      timezone = tz;
    }
  } catch {
    // `Intl` missing / resolvedOptions threw — fall through to "us".
  }
  return regionFromTimezone(timezone);
}
